import { PortfolioModalProps } from '@/types/props/components';
import { toDe } from '@/utilites/sku-operations';
import useTypedTranslation from '@/hooks/useTypedTranslation';
import { ModalsTranslationsKeys } from '@/types/translations/types';
import { getSkuMainImage, SkuMainImage } from '@/utilites/get-sku-main-image';
import { ChangeEvent, Fragment, useEffect, useState } from 'react';
import {
  Dialog,
  DialogPanel,
  DialogTitle,
  Transition,
  TransitionChild,
} from '@headlessui/react';
import Image from 'next/image';
import DefaultButton from '../default/DefaultButton';
import { MdClose } from 'react-icons/md';
import SpiritoryInput from '../Forms/SpiritoryInput';
import { BiEuro } from 'react-icons/bi';
import { PrimaryButton } from '../PrimaryButton';
import { useMutatePortfolio } from '@/useQuery/mutations/portfolio';
import SkuDescription from '@/components/skuDescription';
import { CommonEnum } from '@/types/translations/CommonEnum';

export default function PortfolioModal({
  showPortfolioModal,
  handleClosePortfolioModal,
  skuName,
  skuSlug,
  skuMainImageUrl,
  skuRetailPrice,
  skuBottleSizeInMl,
  skuVolumeInPercent,
  skuReleaseDate,
  showPrevious,
  handlePrevious,
}: PortfolioModalProps) {
  const { addBottlePortfolioMutation } = useMutatePortfolio();
  const { translate } = useTypedTranslation<CommonEnum>('common');
  const { translateWithFallback } =
    useTypedTranslation<ModalsTranslationsKeys>('modals');
  const [amountOfBottles, setAmountOfBottles] = useState<string | number>(1);
  const [purchaseDate, setPurchaseDate] = useState(new Date());
  const [skuRetailPriceValue, setSkuRetailPriceValue] = useState<
    number | string
  >(
    Number(
      toDe(skuRetailPrice, {
        withSymbol: false,
      })
    )
  );

  const setAmount = (e: ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;
    const parsedInputValue = parseInt(inputValue);
    if (isNaN(parsedInputValue) || !inputValue) {
      return setAmountOfBottles('');
    }
    if (parsedInputValue < 1) {
      return setAmountOfBottles('1');
    }
    setAmountOfBottles(parsedInputValue);
  };

  const handleDateChange = (e: ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;
    const parsedInputValue = new Date(inputValue);
    if (parsedInputValue > new Date()) {
      return setPurchaseDate(new Date());
    }
    setPurchaseDate(parsedInputValue);
  };

  const handlePriceChange = (e: ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;
    const parsedInputValue = parseFloat(inputValue);
    if (isNaN(parsedInputValue) || !inputValue) {
      return setSkuRetailPriceValue('');
    }
    setSkuRetailPriceValue(Number(inputValue));
  };

  const handleClose = () => {
    setSkuRetailPriceValue(
      Number(
        toDe(skuRetailPrice, {
          withSymbol: false,
        })
      )
    );
    setAmountOfBottles(1);
    setPurchaseDate(new Date());
    handleClosePortfolioModal();
  };

  const addItemToPortfolio = async (
    e: React.FormEvent<HTMLElement>
  ): Promise<void> => {
    e.preventDefault();
    e.stopPropagation();
    addBottlePortfolioMutation
      .mutateAsync({
        slug: skuSlug,
        purchaseDate: purchaseDate.toISOString(),
        quantity: Number(amountOfBottles),
        purchasePrice: Number(skuRetailPriceValue) * 100,
      })
      .then(() => handleClose())
      .catch(() => {});
  };

  useEffect(() => {
    if (showPortfolioModal) {
      setAmountOfBottles(1);
      setPurchaseDate(new Date());
      setSkuRetailPriceValue(
        Number(
          toDe(skuRetailPrice, {
            withSymbol: false,
          })
        )
      );
    }
  }, [showPortfolioModal, skuRetailPrice]);

  return (
    <Transition appear show={showPortfolioModal} as={Fragment}>
      <Dialog
        as="div"
        className="tw-fixed tw-inset-0 tw-z-[9999] tw-flex tw-items-center tw-justify-center tw-bg-black/50 max-nine:tw-p-2"
        onClose={handleClose}
      >
        <TransitionChild
          as={Fragment}
          enter="tw-ease-out tw-duration-300"
          enterFrom="tw-opacity-0 tw-scale-95"
          enterTo="tw-opacity-100 tw-scale-100"
          leave="tw-ease-in tw-duration-200"
          leaveFrom="tw-opacity-100 tw-scale-100"
          leaveTo="tw-opacity-0 tw-scale-95"
        >
          <DialogPanel className="tw-bg-white tw-rounded-lg tw-shadow-xl tw-w-full tw-max-w-lg tw-p-6">
            <div className="tw-flex tw-justify-between tw-items-center tw-border-b tw-pb-4">
              <DialogTitle className="tw-text-lg tw-font-semibold">
                {translateWithFallback(
                  'portfolio_modal.add_to_portfolio',
                  'Add to your portfolio'
                )}
              </DialogTitle>
              <DefaultButton
                onClick={handleClose}
                className="tw-text-gray-500 tw-hover:text-gray-700"
                aria-label="Close modal"
              >
                <MdClose />
              </DefaultButton>
            </div>

            <div className="tw-mt-4 tw-bg-gray-100 tw-py-2 tw-px-3 tw-rounded-md">
              <div className="tw-flex tw-items-center tw-gap-4">
                <SkuMainImage
                  width={1080}
                  height={1080}
                  src={getSkuMainImage(skuMainImageUrl)}
                  alt={skuName}
                  className="tw-w-16 tw-h-16 tw-border tw-rounded tw-object-contain tw-p-2"
                />
                <div>
                  <span className="tw-font-semibold tw-block">{skuName}</span>
                  <span className="tw-text-sm tw-text-gray-600">
                    <SkuDescription
                      skuBottleSizeInMl={skuBottleSizeInMl}
                      skuVolumeInPercent={skuVolumeInPercent}
                      skuReleaseDate={skuReleaseDate}
                    />
                  </span>
                </div>
              </div>
            </div>

            <form
              onSubmit={addItemToPortfolio}
              className="tw-space-y-4 tw-mt-6"
            >
              <div>
                <SpiritoryInput
                  label={translateWithFallback(
                    'portfolio_modal.amount',
                    'Amount'
                  )}
                  type="text"
                  value={amountOfBottles}
                  onChange={setAmount}
                  required
                  className="tw-w-full tw-border tw-rounded tw-p-2 tw-mt-1"
                />
              </div>

              <div>
                <SpiritoryInput
                  label={translateWithFallback(
                    'portfolio_modal.purchase_date',
                    'Purchase Date'
                  )}
                  type="date"
                  value={purchaseDate.toISOString().split('T')[0]}
                  onChange={handleDateChange}
                  required
                  className="tw-w-full tw-border tw-rounded tw-p-2 tw-mt-1"
                />
              </div>

              <div>
                <SpiritoryInput
                  label={translateWithFallback(
                    'portfolio_modal.price_you_paid',
                    'Price you paid'
                  )}
                  type="text"
                  value={skuRetailPriceValue}
                  onChange={handlePriceChange}
                  required
                  className="tw-flex-grow tw-border-none tw-outline-none"
                  rightIcon={<BiEuro />}
                />
              </div>

              <div className="tw-flex tw-justify-center tw-mt-4 tw-gap-4">
                {showPrevious && (
                  <PrimaryButton outlined={true} onClick={handlePrevious}>
                    {translate(CommonEnum['previous'])}
                  </PrimaryButton>
                )}
                <PrimaryButton type="submit">
                  {translateWithFallback(
                    'portfolio_modal.add_to_portfolio',
                    'Add to portfolio'
                  )}
                </PrimaryButton>
              </div>
            </form>
          </DialogPanel>
        </TransitionChild>
      </Dialog>
    </Transition>
  );
}
